import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Sparkles, Crown, Heart, Video, CalendarHeart, Users, ArrowRight, CheckCircle2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { goToLogin } from "@/lib/auth";
import { api } from "@/lib/api";
import { Logo } from "@/components/Logo";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const Nav = ({ onJoin, user, goHome }) => (
  <nav className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-showty-cream/80 border-b border-border" data-testid="landing-navbar">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Logo className="w-10 h-10" />
        <span className="font-display text-2xl tracking-tight text-showty-navy">Showtynion</span>
      </div>
      <div className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
        <a href="#lab" className="hover:text-showty-red transition-colors">The Lab</a>
        <a href="#events" className="hover:text-showty-red transition-colors">Events</a>
        <a href="#paths" className="hover:text-showty-red transition-colors">Join Paths</a>
      </div>
      {user ? (
        <Button data-testid="nav-dashboard-btn" onClick={goHome} className="rounded-full bg-showty-navy hover:bg-showty-navy/90 text-white px-6">
          Dashboard
        </Button>
      ) : (
        <Button data-testid="nav-join-btn" onClick={onJoin} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white px-6">
          Join the Lab
        </Button>
      )}
    </div>
  </nav>
);

export default function Landing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  const goHome = () => {
    if (!user?.role) navigate("/onboarding");
    else if (user.role === "manager") navigate("/admin");
    else if (user.role === "affiliator") navigate("/affiliator");
    else navigate("/creator");
  };

  const onJoin = () => (user ? goHome() : goToLogin());

  return (
    <div className="min-h-screen bg-showty-cream text-foreground showty-grain">
      <Nav onJoin={onJoin} user={user} goHome={goHome} />

      {/* Hero */}
      <header className="relative pt-32 pb-20 lg:pt-44 lg:pb-28 overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <motion.div className="lg:col-span-6" initial="hidden" animate="show" variants={fadeUp}>
            <div className="inline-flex items-center gap-2 rounded-full border border-showty-gold/50 bg-white/60 px-4 py-1.5 text-xs tracking-widest uppercase text-showty-navy">
              <Sparkles className="w-3.5 h-3.5 text-showty-gold" /> Beauty Creators Lab
            </div>
            <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight text-showty-navy">
              Where beauty<br />creators <span className="text-showty-red italic">thrive</span>.
            </h1>
            <p className="mt-6 text-base sm:text-lg text-muted-foreground max-w-md leading-relaxed">
              Showtynion adalah rumah untuk collab, review & event — tempat 3.000+ beauty creators & affiliator berkembang bersama lewat konten barter eksklusif.
            </p>
            <div className="mt-9 flex flex-wrap items-center gap-4">
              <Button data-testid="hero-join-btn" onClick={onJoin} size="lg" className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white px-8 h-13 text-base group">
                Join the Community
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Button>
              <a href="#paths" className="text-sm font-medium text-showty-navy underline underline-offset-4 decoration-showty-gold">
                Lihat jalur keanggotaan
              </a>
            </div>
            <div className="mt-10 flex items-center gap-6 text-sm text-muted-foreground">
              <span className="flex items-center gap-1.5"><Star className="w-4 h-4 text-showty-gold fill-showty-gold" /> 3.000+ creators</span>
              <span className="flex items-center gap-1.5"><Heart className="w-4 h-4 text-showty-red" /> Exclusive samples</span>
            </div>
          </motion.div>

          <motion.div className="lg:col-span-6 relative" initial="hidden" animate="show" variants={fadeUp} custom={2}>
            <div className="relative rounded-[2rem] overflow-hidden shadow-[0_30px_80px_rgba(27,42,74,0.18)] rotate-1">
              <img src="/images/hero.png" alt="Showtynion beauty creators" className="w-full h-[520px] object-cover object-top" />
              <div className="absolute inset-0 bg-gradient-to-t from-showty-navy/30 to-transparent" />
            </div>
          </motion.div>
        </div>
      </header>

      {/* Lab / About */}
      <section id="lab" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="showty-gold-line w-24 mb-8" />
          <div className="grid lg:grid-cols-3 gap-10">
            <div className="lg:col-span-1">
              <h2 className="font-display text-4xl lg:text-5xl text-showty-navy leading-tight">Inside the<br />Showty Lab</h2>
            </div>
            <div className="lg:col-span-2 grid sm:grid-cols-3 gap-6">
              {[
                { icon: Users, t: "Collab", d: "Terhubung dengan creators & brand untuk kolaborasi konten." },
                { icon: Heart, t: "Review", d: "Dapatkan sample eksklusif untuk direview jujur." },
                { icon: CalendarHeart, t: "Events", d: "Private gathering, community meetup & Skin Journey." },
              ].map((c, i) => (
                <motion.div key={c.t} className="rounded-2xl border border-border bg-white p-7 hover:-translate-y-1 transition-transform duration-300" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid={`lab-card-${i}`}>
                  <c.icon className="w-7 h-7 text-showty-red" />
                  <h3 className="mt-5 font-display text-2xl text-showty-navy">{c.t}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{c.d}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Events */}
      <section id="events" className="py-20 lg:py-28 bg-showty-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
            <div>
              <p className="text-xs tracking-[0.3em] uppercase text-showty-gold">Programs</p>
              <h2 className="mt-3 font-display text-4xl lg:text-5xl">Showty Events</h2>
            </div>
            <p className="text-sm text-white/60 max-w-sm">Program spesial untuk memperkuat komunitas beauty & affiliate kami.</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {events.map((e, i) => (
              <motion.div key={e.id} className="group rounded-2xl overflow-hidden bg-white/5 border border-white/10" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} data-testid={`event-card-${i}`}>
                <div className="h-48 overflow-hidden">
                  <img src={e.image_url} alt={e.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-6">
                  <span className="text-[11px] tracking-widest uppercase text-showty-gold">{e.category}</span>
                  <h3 className="mt-2 font-display text-2xl">{e.title}</h3>
                  <p className="mt-2 text-sm text-white/60 leading-relaxed line-clamp-3">{e.description}</p>
                  <p className="mt-4 text-xs text-white/40">{e.date} · {e.location}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Join Paths */}
      <section id="paths" className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center mb-14">
            <p className="text-xs tracking-[0.3em] uppercase text-showty-red">Choose your path</p>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl text-showty-navy">Satu komunitas, dua jalur</h2>
          </div>
          <div className="grid lg:grid-cols-2 gap-8">
            <motion.div className="rounded-3xl border border-border bg-gradient-to-br from-showty-pink/60 to-white p-9" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} data-testid="path-creator">
              <Crown className="w-9 h-9 text-showty-red" />
              <h3 className="mt-5 font-display text-3xl text-showty-navy">Beauty Creator</h3>
              <p className="mt-3 text-sm text-muted-foreground">Untuk KOL yang fokus membuat konten review & tutorial.</p>
              <ul className="mt-6 space-y-3 text-sm text-showty-navy">
                {["Request Showty sample eksklusif", "Barter: min. 2 video TikTok + IG owning content", "Submit video via link atau upload file", "Approval oleh KOL Manager"].map((x) => (
                  <li key={x} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-showty-red shrink-0 mt-0.5" />{x}</li>
                ))}
              </ul>
              <Button data-testid="path-creator-btn" onClick={onJoin} className="mt-8 rounded-full bg-showty-red hover:bg-[#9e1f24] text-white px-7">Daftar sebagai Creator</Button>
            </motion.div>

            <motion.div className="rounded-3xl border border-border bg-gradient-to-br from-showty-peach/70 to-white p-9" initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={1} data-testid="path-affiliator">
              <Video className="w-9 h-9 text-showty-navy" />
              <h3 className="mt-5 font-display text-3xl text-showty-navy">Affiliator</h3>
              <p className="mt-3 text-sm text-muted-foreground">Untuk affiliate creator Shopee / TikTok yang jualan via konten.</p>
              <ul className="mt-6 space-y-3 text-sm text-showty-navy">
                {["Daftar lewat akun Shopee / TikTok", "Request sample untuk barter", "Barter: 1 video TikTok + Live di akun affiliate", "Konten terpisah dari Beauty Creator"].map((x) => (
                  <li key={x} className="flex gap-2"><CheckCircle2 className="w-4 h-4 text-showty-navy shrink-0 mt-0.5" />{x}</li>
                ))}
              </ul>
              <Button data-testid="path-affiliator-btn" onClick={onJoin} className="mt-8 rounded-full bg-showty-navy hover:bg-showty-navy/90 text-white px-7">Daftar sebagai Affiliator</Button>
            </motion.div>
          </div>
        </div>
      </section>

      <footer className="border-t border-border py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between flex-wrap gap-4 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <Logo className="w-8 h-8" />
            <span className="font-display text-lg text-showty-navy">Showtynion</span>
          </div>
          <p className="font-display text-showty-navy italic">Unite in beauty, empower through confidence.</p>
          <p>© 2026 Showtynion — Beauty Creators Lab</p>
        </div>
      </footer>
    </div>
  );
}
